import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { fmtCurrency } from "../lib/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "$1,000,000" style while typing (whole dollars).
const fmtInput = (raw) => {
  const digits = String(raw).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  return digits ? `$${Number(digits).toLocaleString("en-US")}` : "";
};

// Edit the association's Opening Fund (Beginning Balance) from the Reserve Summary chart.
export default function OpeningBalanceModal({ open, onClose, association, onSaved }) {
  const current = Number(association?.opening_balance || 0);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setValue(fmtInput(String(Math.round(current)))); setError(null); }
  }, [open, current]);

  const save = async () => {
    if (!value) { setError("Enter a balance (use $0 for none)."); return; }
    const ob = Number(String(value).replace(/[$,\s]/g, ""));
    if (!Number.isFinite(ob) || ob < 0) { setError("Opening Fund must be $0.00 or more."); return; }
    setSaving(true); setError(null);
    try {
      await axios.patch(`${API}/admin/associations/${association.aid}`, { opening_balance: ob });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Save failed.");
      setSaving(false);
      return;
    }
    setSaving(false);
    onClose();
    // Parent re-fetches the bundle (scoped to the active scenario) and recalculates.
    if (onSaved) await onSaved({ aid: association.aid });
    try { window.dispatchEvent(new CustomEvent("ureserve:associations-changed")); } catch { /* no-op */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-[380px] rounded-[6px] border border-navy bg-white" data-testid="opening-balance-modal">
        <DialogHeader>
          <DialogTitle className="text-[13px] tracking-[0.14em] uppercase font-bold text-navy">
            Beginning Balance
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate">
            Opening Fund for {association?.name}. Applies to every scenario in this plan year.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between text-[12px] text-slate">
          <span>Current</span>
          <span className="font-mono tabular-nums text-ink" data-testid="opening-balance-current">{fmtCurrency(current, 0)}</span>
        </div>
        <label className="text-[11px] tracking-[0.1em] uppercase font-semibold text-navy" htmlFor="ob-input">
          New Balance ($)
        </label>
        <input
          id="ob-input"
          autoFocus
          inputMode="numeric"
          placeholder="$0"
          value={value}
          onChange={(e) => setValue(fmtInput(e.target.value))}
          onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          className="w-full border border-rule rounded-[3px] px-3 py-2 font-mono tabular-nums text-right text-[14px] focus:outline-none focus:border-navy"
          data-testid="opening-balance-input"
        />
        {error && <div className="text-[12px] text-coral" data-testid="opening-balance-error">{error}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-[12px] rounded-[3px] border border-rule text-ink hover:bg-navy-wash transition-colors"
            data-testid="opening-balance-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-3 py-1.5 text-[12px] rounded-[3px] bg-navy text-white hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="opening-balance-save"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
